import { React, useState, useEffect, useRef } from 'react';
import { clickable } from "../../utils/a11y";
import { useDialog } from "../../utils/useDialog";
import "../../styles/Patient-ui/Patient-home.css";
import { useNavigate } from 'react-router-dom';
import "../../styles/Patient-ui/Patient-Homepage.css";
import PatientSymptomChart from './Patient-symptom-chart';
import Loader from '../../utils/loader';
import { canAssessAppt } from '../../utils/symptomAssessment';
import axios from 'axios';
import Popup from 'reactjs-popup';
import SymptomCalculator from './SymptomCalculator';
import Cookies from "js-cookie";
import { API_DETAILS, COOKIE_KEYS } from '../../App';
import CarePlan from './PatientCareplan';
import { toast } from '../../utils/toast';
import { useSubmitGuard } from '../../utils/useSubmitGuard';
import { errorMessage } from '../../utils/errorMessage';
import { openInNewTab } from '../../utils/openInNewTab';
import Button from '@mui/material/Button';
import { getSeverityTier, getScoreColor } from '../../utils/symptomSeverity';
import DeleteIcon from '@mui/icons-material/Delete';

// The "patient doesn't know their diagnosis" record (diagnosis id 21) has been
// relabelled more than once — originally "I don't know my diagnosis yet!", then
// "Not Listed", now "Unsure of my diagnosis". Appointments carry the diagnosis
// name they were booked under, so historical rows still hold the older labels.
// Match all of them, otherwise those patients silently lose the "Describe Your
// Symptoms" prompt and get offered a symptom score they never took.
const UNSURE_DIAGNOSIS_NAMES = [
  "unsure of my diagnosis",
  "not listed",
  "i don't know my diagnosis yet!",
];

const isUnsureDiagnosis = (diagName) =>
  UNSURE_DIAGNOSIS_NAMES.includes(String(diagName ?? "").trim().toLowerCase());

// PatientView unmounts each screen when you switch tabs, so the chosen condition
// was lost on every navigation and the patient was dropped back on the first one.
// Remember it for the session — sessionStorage rather than localStorage so it
// clears when the browser closes, and wrapped because it throws in private mode.
const REMEMBERED_DIAGNOSIS_KEY = "youro.home.selectedDiagName";

const rememberDiagnosis = (diagName) => {
  try {
    if (diagName) sessionStorage.setItem(REMEMBERED_DIAGNOSIS_KEY, diagName);
  } catch (e) {
    /* storage unavailable — fall back to default selection */
  }
};

const recallDiagnosis = () => {
  try {
    return sessionStorage.getItem(REMEMBERED_DIAGNOSIS_KEY);
  } catch (e) {
    return null;
  }
};

/*
 * Defined at module scope, not inside PatientHomePage. As a nested component it
 * was a brand new component type on every parent render, so React unmounted and
 * remounted it: an open "Describe Your Symptoms" box lost whatever the patient
 * had typed whenever anything on the dashboard re-rendered.
 */
const UpcomingAppointments = ({ activeAppts, changeView, onScoreClick, onFillSymptomScore, onCancel, guard, loadFailed, onRetry }) => {


  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedApptId, setSelectedApptId] = useState(null);
  const [descriptionText, setDescriptionText] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState(null);

  const [descriptionLoading, setDescriptionLoading] = useState(false);
  const [descriptionSavedAt, setDescriptionSavedAt] = useState(null);

  const handleDescribeDiagnosisClick = async (apptId, patientId) => {
    setSelectedApptId(apptId);
    setSelectedPatientId(patientId);
    setDescriptionText('');
    setDescriptionSavedAt(null);
    setIsModalOpen(true);

    // Load anything already written for this appointment so the patient can
    // read it back and revise it, rather than being handed a blank box and
    // unknowingly replacing what they said last time.
    setDescriptionLoading(true);
    try {
      const res = await axios.get(
        API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension +
        `/diagnosisDescription/${apptId}`
      );
      if (res.data?.description) {
        setDescriptionText(res.data.description);
        setDescriptionSavedAt(res.data.lastUpdated || null);
      }
    } catch (err) {
      // An older description that predates author tracking will not come back.
      // Leave the box empty rather than blocking them from writing a new one.
    } finally {
      setDescriptionLoading(false);
    }
  };

  const handleSubmitDescription = guard(async () => {
      setDescriptionLoading(true);
      const api = "/saveDiagnosisDescription";

      const url =
          API_DETAILS.baseUrl +
          API_DETAILS.PORT +
          API_DETAILS.baseExtension +
          api;

      const config = {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Methods": "*",
          "Content-Type": "application/json",
        },
      };

    try {

      await axios.post(url, {
            patientId: selectedPatientId,
            apptId: selectedApptId,
            // Plain text, not markup. What the patient types is a description of
            // their symptoms; how a stored note is built out of it is the server's
            // decision, and sending "<p>" from here made every browser a co-author
            // of the HTML a provider ends up reading.
            description: descriptionText,
          }, config);

      toast.success("Your symptom description is saved. Your provider will see it before your appointment.");
      setIsModalOpen(false);
      setDescriptionText('');
    } catch (err) {
      toast.error(errorMessage(err, "We couldn't save your symptom description. Your text is still here, so try again in a moment."));
    } finally {
      setDescriptionLoading(false);
    }
  });

  const closeModal = () => {
    setIsModalOpen(false);
    setDescriptionText('');
  };
  const describeDialogRef = useDialog(isModalOpen, closeModal);


  return (
    <div style={{ height: '100%' }}>
      {activeAppts.length === 0 && loadFailed && (
        <div className='new-ua' style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
          <h3 style={{ margin: 0 }}><i>We couldn't load your appointments</i></h3>
          <p style={{ margin: 0, textAlign: 'center' }}>This is a problem on our end, not a sign that you have none booked.</p>
          <button type="button" className='btn-filled' onClick={onRetry} style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px' }}>Try again</button>
        </div>
      )}

      {activeAppts.length === 0 && !loadFailed && (
        <div className='new-ua' style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
          <h3 style={{ margin: 0 }}><i>No upcoming appointments</i></h3>
          <button type="button" className='btn-book' onClick={() => changeView(1)}>Book an appointment</button>
        </div>
      )}

      {activeAppts.map((item) => (
        <div key={item.apptId} className='upcoming-appointment' >
          <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>
            {item.diagName}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <span style={{ opacity: 0.7 }}>Date</span>
              <span>{new Date(item.apptStartTime).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <span style={{ opacity: 0.7 }}>Time</span>
              <span>{new Date(item.apptStartTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <span style={{ opacity: 0.7 }}>Provider</span>
              <span>{item.doctorName}</span>
            </div>
          </div>
          <div style={{ fontSize: '0.9rem', width: '100%' }}>
            {isUnsureDiagnosis(item.diagName) ? (
              <span
                style={{ textDecoration: 'underline', color: 'var(--youro-sky)', cursor: 'pointer' }}
                onClick={() => handleDescribeDiagnosisClick(item.apptId, item.patientId)}
              >
                Describe Your Symptoms
              </span>
            ) : item.symptomScore !== 0 ? (
              <span
                style={{ textDecoration: 'underline', color: 'var(--youro-sky)', cursor: 'pointer' }}
                onClick={() => onScoreClick(item.diagName, item.symptomScore)}
              >
                View Symptom Score
              </span>
            ) : canAssessAppt(item, activeAppts) ? (
              /* The only route to the assessment used to be the symptom chart,
                 which is driven by the "Viewing care for" selector — and that
                 selector only lists conditions the patient already has a care
                 plan for. A first appointment for a new condition therefore had
                 no way in at all. Offering it on the appointment itself works
                 for both, and needs no diagnosis to be selected first. */
              <button
                onClick={() => onFillSymptomScore(item.diagName, item.diagId, item.apptId)}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                style={{
                  width: '100%',
                  marginTop: '4px',
                  backgroundColor: 'var(--youro-mint)',
                  color: 'var(--youro-sage)',
                  border: '1px solid var(--youro-sage)',
                  borderRadius: '6px',
                  height: '40px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Take Symptom Assessment
              </button>
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: '12px', paddingTop: '16px', width: '100%' }}>
            {item.link ? (
              <button
                onClick={() => openInNewTab(`${item.link}`)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                style={{ flex: 1, backgroundColor: '#ff6f61', color: '#ffffff', border: 'none', borderRadius: '6px', height: '40px', cursor: 'pointer', fontWeight: '500', transition: 'all 0.3s ease-in-out' }}
              >
                Join Video Call
              </button>
            ) : (
              <div className="tooltip-container" style={{ flex: 1, display: 'flex' }}>
                <button
                  title="Your provider hasn't added the video link yet."
                  style={{ flex: 1, backgroundColor: '#888', color: '#ffffff', border: 'none', borderRadius: '6px', height: '40px', cursor: 'not-allowed', fontWeight: '500', width: '100%' }}
                >
                  Join Video Call
                </button>
              </div>
            )}
            <button
              onClick={() => onCancel(item)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#cfe8d4';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#faf7f2';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              style={{ flex: 1, border: '1px solid #2b7a9b', backgroundColor: '#faf7f2', color: '#2b7a9b', borderRadius: '6px', height: '40px', cursor: 'pointer', fontWeight: '500', transition: 'all 0.3s ease-in-out' }}
            >
              Cancel
            </button>
          </div>
        </div>
      ))}

     {isModalOpen && (
       <div style={{
         position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
         backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
         justifyContent: 'center', alignItems: 'center', zIndex: 1000
       }}>
         <div
           ref={describeDialogRef}
           role="dialog"
           aria-modal="true"
           aria-labelledby="describe-symptoms-title"
           tabIndex={-1}
           style={{
           background: '#fff', padding: '20px', borderRadius: '8px',
           width: '90%', maxWidth: '500px'
         }}>
           <h3 id="describe-symptoms-title" style={{ marginBottom: '4px' }}>Describe Your Symptoms</h3>
           <p style={{ margin: '0 0 10px', fontSize: '0.8rem', opacity: 0.7 }}>
             {descriptionLoading
               ? 'Loading what you wrote before…'
               : descriptionSavedAt
               ? `You last updated this on ${new Date(descriptionSavedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. Edit it below and submit to replace it.`
               : 'Your provider will read this before your appointment. You can come back and change it any time.'}
           </p>
           <textarea
             style={{ width: '100%', height: '200px', marginBottom: '10px' }}
             aria-label="Your symptoms"
             placeholder="Describe your symptoms here..."
             value={descriptionText}
             disabled={descriptionLoading}
             onChange={(e) => setDescriptionText(e.target.value)}
           />
           <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
             <button className='p-join-now' onClick={closeModal} style={{ width: 'fit-content', margin: '0px 10px 10px 10px', padding: '2px 10px', cursor: 'pointer' }}>
               Cancel
             </button>
             <button
               className='p-join-now'
               onClick={handleSubmitDescription}
               disabled={descriptionLoading || !descriptionText.trim()}
               style={{ width: 'fit-content', margin: '0px 10px 10px 10px', padding: '2px 10px', cursor: descriptionLoading || !descriptionText.trim() ? 'not-allowed' : 'pointer', opacity: descriptionLoading || !descriptionText.trim() ? 0.6 : 1 }}
             >
               {descriptionSavedAt ? 'Save Changes' : 'Submit'}
             </button>
           </div>
         </div>
       </div>
     )}


    </div>
  );
};

const PatientHomePage = (props) => {
  const uId = Cookies.get(COOKIE_KEYS.userId);//1;
  const userName = Cookies.get(COOKIE_KEYS.userName);
  const [prevAppts, setPrevAppts] = useState([]);
  const [upComingAppts, setUpcomingAppts] = useState([]);
  const [viewVal, setViewVal] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedCareplan, setSelectedCareplan] = useState(null);
  const [activeLoader, setActiveLoader] = useState(false);
  const [, guard] = useSubmitGuard();
  const [fetchSymptomScore, setFetchSymptomScore] = useState(false);
  const [cancelPopupOpen, setcancelPopupOpen] = useState(false);
  const [selectedDiagId, setSelectedDiagId] = useState('');
  const [selectedApptId, setSelectedApptId] = useState(null);
  const [chooseDiagnosis,setChooseDiagnosis] = useState(false);
  const [selectedDiagName,setSelectedDiagName] = useState('');
  const [isIntakeForm, setIsIntakeForm] = useState(false);
  const [showSeverity, setShowSeverity] = useState(false);
  const [symptomScore, setSymptomScore] = useState(0);
  const [selectedOptionName, setSelectedOptionName] = useState('');
  const [severityDescription, setSeverityDescription] = useState("");
  const [carePlanData, setCarePlanData] = useState([]);
  const [selectedCarePlan, setSelectedCarePlan] = useState(null);
  const [mixViews, setMixViews] = useState([]);

  const [diagnosisNames, setDiagnoses] = useState([]);
  const [prevDIag, setPrevDiag] = useState([]);
  const [selectedOption, setSelectedOption] = useState('');
  const [render, canRender] = useState(false)
  const [data, setData] = useState([]);
  const [score, setScore] = useState(0);
  const [selectedScore, setSelectedScore] = useState([]);
  const [dropdownInputs, setDropdownInputs] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  // Each fetcher below swallows its own error so one failure doesn't abort the
  // others. That left an API outage looking exactly like an empty account:
  // "No record of appointments". This flag lets the screen say what happened.
  const [loadFailed, setLoadFailed] = useState(false);

  const navigate = useNavigate();

  const navToProfile = (viewVal) => {
    props.changeView(viewVal);
  }

  useEffect(() => {
    fetchData();
  }, [viewVal]);

  useEffect(()=>{
  },[selectedDiagId]);

  useEffect(() => {
      if (prevDIag && prevDIag[0]) {
        setSelectedOption(prevDIag[0].diagId);
        handleSelectChange({ target: { value: prevDIag[0].diagId } });
      }
    }, [prevDIag && prevDIag[0]]);

  useEffect(() => {
    if (data.length && dropdownInputs) {
      const { diagData, carePlanData } = dropdownInputs;
      generateDropdownData(diagData, carePlanData);
    }
  }, [data, dropdownInputs]);

  const fetchData = async () => {
    setActiveLoader(true);
    setLoadFailed(false);
    if (viewVal === 4 || viewVal === 5 || viewVal === 6) {
      navToProfile(viewVal);
    } else {
      try {
        const [
          intakeStatusRes,
          appointmentsRes,
          carePlanDataRes,
          prevDiagRes,
          allDiagRes,
          prevSymptomScoreRes
        ] = await Promise.all([
          fetchIntakeFormStatus(),
          fetchPrevAndUpcomingAppointments(),
          fetchCarePlanData(),
          fetchPrevDiag(),
          fetchAllDiagnoses(),
          fetchPrevSymptomScore()
        ]);

        setDropdownInputs({
          diagData: prevDiagRes?.data,
          carePlanData: carePlanDataRes?.data
        });
      } catch (error) {
      } finally {
        setActiveLoader(false);
      }
    }
  };

  const generateDropdownData = (diagDataRaw = [], carePlanDataRaw = []) => {
    const diagData = diagDataRaw || [];
    const carePlanData = carePlanDataRaw || [];

    const mixList = [];

    const carePlanNames = carePlanData.map(cp => cp.diagName);
    const diagNames = diagData.map(d => d.diagName);

    carePlanData.forEach(cp => {
      const match = diagData.find(d => d.diagName === cp.diagName);

      if (match) {
        mixList.push({
          diagId: match.diagId,
          ...cp
        });
      } else {
        mixList.push({
          diagId: null,
          ...cp
        });
      }
    });

    // Diagnoses without a care plan are deliberately left out. This selector is
    // for reviewing care the patient has actually received, and a diagnosis with
    // no care plan has nothing behind it — every clinical field would be null, so
    // it offered an empty care plan and a symptom score that was never taken.

    setMixViews(mixList);
    // mixList can now legitimately be empty — a patient with no care plans yet —
    // so this must not index into it blindly.
    if (mixList.length > 0) {
      // Restore the condition the patient was last looking at, if it is still one
      // of their care plans; otherwise fall back to the first.
      const remembered = recallDiagnosis();
      const initial =
        mixList.find((plan) => plan.diagName === remembered) || mixList[0];
      setSelectedCarePlan(initial);
      setSelectedOptionName(initial.diagName);
      showScoreForSelectedDiagnosis(initial.diagId);
    }
    setScore(0);
  };

  const fetchIntakeFormStatus = async () => {
    const url = API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + `/intakeFormStatus/${uId}`;
    const config = {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': '*',
        'Content-Type': 'application/json'
      }
    };

    try {
      const res = await axios.get(url, config);
      if (res.data === false){
        // TODO: Better way to pass diagId 8?
        handleFillSymptomScoreClick("Intake form", 8, 0);
      }

    }
    catch (err) {
      // Not shown as an appointments error: this is the intake-form check.
    }
  }

  const fetchPrevAndUpcomingAppointments = async () => {
    const url = API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + `/appointments/${uId}?timeZone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`;
    const config = {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': '*',
        'Content-Type': 'application/json'
      }
    };
    try {
      const res = await axios.get(url, config);
      // setPrevAppts(res.data.previousAppointments);
      setUpcomingAppts(res.data.upComingAppointments);
    }
    catch (err) {
      setLoadFailed(true);
    }
  }

  const fetchCarePlanData = async () => {
    const patientId = Cookies.get(COOKIE_KEYS.userId);
    const url = API_DETAILS.baseUrl+ API_DETAILS.PORT + API_DETAILS.baseExtension +`/getAllCarePlanByPatient?patientId=${patientId}`;
    const config = {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': '*',
        'Content-Type': 'application/json'
      }
    };

    try {
      const res = await axios.get(url, config);
      setCarePlanData(res.data);
      if (res.data.length > 0) {
        setSelectedCarePlan(res.data[0]);
      }
      return res;
    } catch (err) {
      // A patient with no care plan yet gets 400 "No Care Plan Exists". That is
      // an empty list, not a failure — and it is not the appointments request,
      // so it must not show "We couldn't load your appointments".
      setCarePlanData([]);
      return null;
    }
  }

  const handleChange = (e) => {
    const selected = carePlanData.find(plan => plan.diagName === e.target.value);
    setSelectedCarePlan(selected);
  };

  const youroHeaderRef = useRef();

  const handleFetchNotifications = () => {

    if (youroHeaderRef.current) {
      youroHeaderRef.current.fetchNotifications();
    }
  };

  const [deleteItem, setDeleteItem] = useState('');

  // Opens the confirmation popup below. The Cancel button used to call
  // cancelAppointment straight away, so one click cancelled the appointment.
  const confirmCancelAppointment = (item) => {
    setDeleteItem(item);
    setcancelPopupOpen(true);
  };

  const handleSubmit = () => {
    cancelAppointment(deleteItem);
    setcancelPopupOpen(false);
  };

  const handleFillSymptomScoreClick = (diagName,diagId,apptId) => {
    // Previously warned "Pleaser take the Intake Form to proceed further" on
    // every click, for every diagnosis — a typo, and wrong besides: this opens
    // the symptom assessment, not the intake form, and it opens immediately, so
    // there is nothing for the patient to go and do first.
    const slug = diagName.toLowerCase().replace(/\s+/g, '-');
    // console.log("slug with apptId, diagId.........", slug, apptId, diagId);
    navigate(`/assessment/${slug}`, {
      state: {
        apptId: apptId,
        diagId: diagId
      }
    });
  }


  const cancelAppointment = guard(async (data) => {

    // The second segment is who is cancelling: the patient themselves. This sent
    // the provider's id, which the server refuses from a patient, so patients
    // could not cancel on the website at all. (Backend #39 takes the canceller
    // from the session and ignores this; servers without it still check it.)
    const url = API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + `/cancelAppointment/${data.apptId}/${uId}`;
    const config = {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': '*',
        'Content-Type': 'application/json'
      }
    };

    setActiveLoader(true);
    try {
      const res = await axios.put(url, config);
      fetchPrevAndUpcomingAppointments()
      handleFetchNotifications();
      toast.success("Appointment cancelled. We've let your provider know.");
    }
    catch (err) {
      toast.error(errorMessage(err, "We couldn't cancel that appointment. Try again in a moment."));
    } finally {
      setActiveLoader(false);
    }
  });


  const getSeverityDescription = (score) => {
      const tier = getSeverityTier(score);
      return (
        <>
          <h2 style={{ color: tier.textColor }}>{tier.label} Symptoms</h2>
          <p style={{ fontSize: "20px" }}>{tier.description}</p>
        </>
      );
    };

  const handleScoreClick = (diagName, score) => {
    setSelectedOptionName(diagName);
      setSymptomScore(score);
      setSeverityDescription(getSeverityDescription(score));
      setShowSeverity(true);
    };

  useEffect(() => {
    if (fetchSymptomScore) {
      const fetchAndUpdateMixObjects = async () => {
        const [diagRes, carePlanRes] = await Promise.all([
          fetchPrevDiag(),
          fetchPrevSymptomScore()
        ]);

        generateDropdownData(diagRes?.data, carePlanRes?.data);
        setFetchSymptomScore(false);
      };

      fetchAndUpdateMixObjects();
    }
  }, [fetchSymptomScore]);

  const fetchAllDiagnoses = async () => {
    const url = API_DETAILS.baseUrl+ API_DETAILS.PORT + API_DETAILS.baseExtension +`/getAllDiagnoses`;
    const config = {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': '*',
        'Content-Type': 'application/json'
      }
    };
    try {
      const res = await axios.get(url, config);
      setDiagnoses(res.data);
    }
    catch (err) {
    }
  };

  const fetchPrevDiag = async () => {
    const url = API_DETAILS.baseUrl+ API_DETAILS.PORT + API_DETAILS.baseExtension +`/getDiagnosisByCustomer/${uId}`;
    const config = {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': '*',
        'Content-Type': 'application/json'
      }
    };

    try {
      const res = await axios.get(url, config);
      setPrevDiag(res.data);
      return res;
    }
    catch (err) {
      return null;
    }
  }

  const handleSelectChange = (event) => {
    const value = event.target.value;
    setSelectedOption(value);
    showScoreForSelectedDiagnosis(value);
    const diag = diagnosisNames.find(diagnosis => diagnosis.diagId === Number(value))?.name || '';
    setSelectedOptionName(diag);
    setScore(0);
  };

  const handleDropdownChange = (event) => {
      const value = event.target.value;
      const selected = mixViews.find(plan => plan.diagName === value);
      setSelectedCarePlan(selected);
      rememberDiagnosis(value);

      const diagId = prevDIag.find(diag => diag.diagName === value)?.diagId;
      setSelectedOption(diagId);
      showScoreForSelectedDiagnosis(diagId);
      setSelectedOptionName(value);
      setScore(0);
    };

  const fetchPrevSymptomScore = async () => {
    const url = API_DETAILS.baseUrl+ API_DETAILS.PORT + API_DETAILS.baseExtension +`/symptomScore/${uId}`;
    const config = {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': '*',
        'Content-Type': 'application/json'
      }
    };
    try {
      const res = await axios.get(url, config);
      setData(res.data);
      canRender(true);
      return res.data;
    } catch (err) {
      return null;
    }
  }

  const showScoreForSelectedDiagnosis = (value) => {
    if (value !== '') {

      const scores = data.filter(d => d.diagnosisId == value).map(score => {
        return {
          ...score,
          unix: new Date(score.dateTime).valueOf()
        }
      })
      setSelectedScore(scores);
    }
  }

  const [open, setOpen] = useState(false);
  const activeAppts = upComingAppts?.filter(item => item.status !== 'CANCELED') || [];

  // Without a subscription the app does very little, and until now the only sign
  // of that was a "Renew now" link on the scheduling screen — wording that makes
  // no sense to someone who has never subscribed, on a screen they may not reach.
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (!uId) return;
    const load = async () => {
      try {
        const res = await axios.get(
          API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension +
          `/getSubscriptionDetails/${uId}`
        );
        setSubscription(res.data || null);
      } catch (err) {
        // Leave the banner hidden rather than nagging a paying member because a
        // request failed.
      }
    };
    load();
  }, [uId]);

  const needsSubscription = subscription && subscription.isActive === false;
  const isReturningMember = Boolean(subscription?.hasEverSubscribed);

  return (
    //  <div className='hm'>

    <div className='care-plan'>
      {/* <div className='header'>
        <h1 style={{ marginLeft: '50px' }}>youro</h1>
         <Popmenu/>
         </div>  */}

      <div className="patient-page" style={{ padding: '0.5rem 4rem 2rem 2rem', flex: '1', width: '100%', height: '85%' }}>
            {needsSubscription && (
              <div
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: '1rem', flexWrap: 'wrap',
                  padding: '16px 20px', marginBottom: '20px',
                  borderRadius: '10px',
                  backgroundColor: 'var(--youro-mint)',
                  border: '1px solid var(--youro-sage)',
                }}
              >
                <div>
                  <p style={{ margin: 0, fontWeight: 600 }}>
                    {isReturningMember
                      ? 'Your subscription has ended'
                      : 'Activate your subscription'}
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: '0.875rem', opacity: 0.8 }}>
                    {isReturningMember
                      ? 'Renew to book appointments, message your care team, and track your symptoms again.'
                      : 'Youro needs an active subscription to book appointments, message your care team, and track your symptoms.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/pricing')}
                  style={{
                    backgroundColor: 'var(--youro-coral)', color: '#ffffff',
                    border: 'none', borderRadius: '8px',
                    padding: '10px 20px', fontSize: '0.875rem', fontWeight: 600,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  {isReturningMember ? 'Renew Subscription' : 'Activate Subscription'}
                </button>
              </div>
            )}

            {subscription?.isActive && !activeLoader && !loadFailed && activeAppts.length === 0 && (
              <div className="home-book-prompt">
                <div>
                  <p className="home-book-prompt-title">
                    {carePlanData.length > 0 ? 'Book your next appointment' : 'Book your first appointment'}
                  </p>
                  <p className="home-book-prompt-text">
                    {carePlanData.length > 0
                      ? 'You have nothing booked. Pick a time with your urology provider, usually within a day or two.'
                      : 'Meet a urology-trained provider by video. Your care plan and prescriptions start here.'}
                  </p>
                </div>
                <button type="button" className="btn-book" onClick={() => props.changeView(1)}>Book an appointment</button>
              </div>
            )}

            <div className="home-header" style={{ justifyContent: 'space-between', alignItems: 'center', display: 'flex', marginBottom: '20px', width: '100%' }}>
                <p style={{ margin: '0', fontSize: '28px', fontWeight: '500' }}>Welcome back, {String(userName || '').split(' ')[0]}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', paddingInline: '1rem', paddingBlock: '0.4rem', borderRadius: '0.6rem', backgroundColor: '#cfe8d4', border: '2px solid #8fa87a' }}>
                    <label style={{margin: '0'}} >Viewing care for:</label>
                    {activeLoader ? (
                       <div style={{ position: 'relative', width: '280px' }}>
                       <select
                         disabled
                         style={{
                           borderColor: '#ccc',
                           width: '100%',
                           borderRadius: '8px',
                           borderStyle: 'solid',
                           borderWidth: '1px',
                           backgroundColor: '#f9f9f9',
                           paddingInline: '0.75rem',
                           paddingBlock: '0.5rem',
                           fontSize: '0.8rem',
                           color: '#888',
                           appearance: 'none'
                         }}
                       >
                         <option>Loading</option>
                       </select>

                       <span className="dots-container" style={{
                         position: 'absolute',
                         left: '62px',
                         top: '7px',
                         fontSize: '0.8rem',
                         color: '#888',
                         pointerEvents: 'none'
                       }}></span>
                     </div>
                    ) :  mixViews && mixViews.length > 0 ? (
                      <div className="custom-select-container">
                        {/* Trigger Button */}
                        <div
                          className="custom-select-trigger"
                          style={{
                            boxShadow: isFocused ? "0 0 0 3px rgba(143, 168, 122, 0.6)" : "none",
                            borderColor: "var(--youro-sage)"
                          }}
                          tabIndex={0}
                          role="button"
                          aria-haspopup="listbox"
                          aria-expanded={isOpen}
                          aria-label={`Viewing care for ${selectedCarePlan?.diagName || "no condition selected"}`}
                          // It opened on click only; a keyboard user could focus it
                          // but never change which condition they were viewing.
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
                              e.preventDefault();
                              setIsOpen(true);
                            } else if (e.key === "Escape") {
                              setIsOpen(false);
                            }
                          }}
                          onClick={() => setIsOpen(!isOpen)}
                          onFocus={() => setIsFocused(true)}
                          onBlur={(e) => {
                            setIsFocused(false);
                            // Tabbing from the trigger into an option is not leaving
                            // the selector; closing then would drop the option.
                            if (e.currentTarget.parentElement.contains(e.relatedTarget)) return;
                            setTimeout(() => setIsOpen(false), 200);
                          }}
                        >
                          <span>{selectedCarePlan?.diagName || "Select Diagnosis"}</span>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2">
                            <path d="m6 9 6 6 6-6"/>
                          </svg>
                        </div>

                        {/* Dropdown Menu */}
                        {isOpen && (
                          <div
                            className="custom-options-menu"
                            role="listbox"
                            aria-label="Conditions"
                            onBlur={(e) => {
                              if (!e.currentTarget.parentElement.contains(e.relatedTarget)) setIsOpen(false);
                            }}
                          >
                            {mixViews.map((result) => {
                              const isSelected = result.diagName === selectedCarePlan?.diagName;
                              const choose = () => {
                                handleDropdownChange({ target: { value: result.diagName } });
                                setIsOpen(false);
                              };
                              return (
                                <div
                                  key={result.diagName}
                                  className={`custom-option ${isSelected ? 'selected' : ''}`}
                                  role="option"
                                  aria-selected={isSelected}
                                  tabIndex={0}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      choose();
                                    } else if (e.key === "Escape") {
                                      setIsOpen(false);
                                    }
                                  }}
                                  onClick={choose}
                                >
                                  {result.diagName}
                                  {isSelected && (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
                                      <path d="M20 6 9 17l-5-5"/>
                                    </svg>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ position: 'relative', width: '280px' }}>
                       <select
                         disabled
                         style={{
                           borderColor: '#ccc',
                           width: '100%',
                           borderRadius: '8px',
                           borderStyle: 'solid',
                           borderWidth: '1px',
                           backgroundColor: '#f9f9f9',
                           paddingInline: '0.75rem',
                           paddingBlock: '0.5rem',
                           fontSize: '0.8rem',
                           color: '#888',
                           appearance: 'none'
                         }}
                       >
                         <option>No data</option>
                       </select>

                     </div>
                    )}
                </div>
            </div>

              <div className='all-details'>
                <div className='care-plan-details-patient' style={{ border: '2px solid rgba(46, 46, 46, 0.1)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <h2 style={{ margin: '0px', display: 'flex', alignItems: 'center', gap: '8px' }} className='care-plan-h2'>
                      Current Care Plan
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-activity w-6 h-6"
                        aria-hidden="true"
                        style={{ color: '#8fa87a' }}
                      >
                        <path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2"></path>
                      </svg>
                    </h2>
                  </div>
                  <h6 style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '8px',
                      minHeight: '1.2em',
                  }}>
                    {activeLoader ? (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        Fetching diagnosis name
                        <span className="dots-container" style={{
                          display: 'inline-block',
                          marginLeft: '2px',
                          width: '20px',
                          textAlign: 'left',
                          left: '62px',
                          top: '10px',
                          fontSize: '0.8rem',
                          color: '#000',
                          pointerEvents: 'none'
                        }}></span>
                      </div>
                    ) : (
                      selectedCarePlan?.diagName || "\u00A0"
                    )}
                  </h6>
                  {/* {isLoading? (
                   <div className="loader-container">
                    <div className="loader"></div>
                     <div>Loading...</div>
                   </div>
                 ) : ( <CarePlan/>)} */}
                  <div className = 'div-below-header'>
                    <CarePlan changeView={props.changeView} carePlanData={selectedCarePlan} />
                  </div>
                </div>
                <div className='column-data'>
                  <PatientSymptomChart uId={uId} retakeSymptomScore={setOpen} fetchSymptomScore={fetchSymptomScore} setFetchSymptomScore={setFetchSymptomScore} setIsIntakeForm={setIsIntakeForm} doctorView={false} selectedOption={selectedOption} prevDIag={prevDIag} render={render} data={data} fetchPrevDiag={fetchPrevDiag} score={score} setScore={setScore} selectedScore={selectedScore} onFillSymptomClick={handleFillSymptomScoreClick} activeAppts={activeAppts} />
                  <div className='row-data' style={{ marginTop: '1%', height: '51.5%' }}>
                    <div className='care-plan-details-patient-1' style={{ marginRight: '2%', border: '2px solid rgba(46, 46, 46, 0.1)' }}>
                      <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }} className = "appointment-header-1">
                        Upcoming Appointments
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-calendar w-6 h-6"
                          aria-hidden="true"
                          style={{ color: '#8fa87a' }}
                        >
                          <path d="M8 2v4"></path>
                          <path d="M16 2v4"></path>
                          <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                          <path d="M3 10h18"></path>
                        </svg>
                      </h3>
                      <div className='care-plan-details-patient-1-scroll-section'>
                        <UpcomingAppointments
                          activeAppts={activeAppts}
                          loadFailed={loadFailed}
                          onRetry={fetchData}
                          changeView={props.changeView}
                          onScoreClick={handleScoreClick}
                          onFillSymptomScore={handleFillSymptomScoreClick}
                          onCancel={confirmCancelAppointment}
                          guard={guard}
                        />
                      </div>

                    </div>
                    {/* <div className='care-plan-details-patient-1' style={{ border: '2px solid rgba(46, 46, 46, 0.1)', marginRight: '0px' }}>
                      <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} className = "appointment-header-1">Previous Appointments</h3>
                      <div className='care-plan-details-patient-1-scroll-section'>
                        <PreviousAppointments />
                      </div>
                    </div>
                    */ }
                  </div>
                </div>
                <Loader active={activeLoader} />
              </div>
      </div>

      {open && <SymptomCalculator uId={uId} open={open} setOpen={setOpen} fetchSymptomScore={setFetchSymptomScore} diagId={selectedDiagId} apptId={selectedApptId} setDiagId={setSelectedDiagId} choosenDiagnosis = {chooseDiagnosis} diagName = {selectedDiagName} isIntakeForm={isIntakeForm} fromLandingPage={false} onSubmitRefresh={fetchData}/>}

      <Popup open={popupOpen} modal closeOnDocumentClick={false} onClose={() => setPopupOpen(false)} className="congrats-popup">
        <div style={{ position: 'absolute', top: '20px', right: '20px', cursor: 'pointer' }} {...clickable(() => { setPopupOpen(false); props.changeView(0) }, 'Close')}>
          <span className="material-symbols-outlined">
            close
          </span>
        </div>
        <div style={{ padding: '50px 20px' }}>
          <CarePlan apptId={selectedCareplan && selectedCareplan.apptId} />
        </div>
      </Popup>

      {/* <div className="notification-popup-overlay" > */}
        <Popup
          open={cancelPopupOpen}
          closeOnDocumentClick
          onClose={() => setcancelPopupOpen(false)}
          modal
          className="notification-popup-overlay"
        >
          <div className="popup-content-admin">
            <div className='row'>
              <div className='col-12 info-col'>
                <h3 style={{ marginLeft: '5%' }}>Are you sure you want to cancel the appointment?</h3>
              </div>
              <div className='col-12 buttons-col row'>
                <div className='col-6'></div>
                <div className='col-6'>
                  <Button className='btn-outlined' onClick={() => setcancelPopupOpen(false)} variant="outlined" style={{ marginRight: '10px' }} >
                    Back
                  </Button>
                  <Button className='btn-filled' onClick={handleSubmit} variant="contained" startIcon={<DeleteIcon />}>
                    Cancel
                  </Button>
                </div>
              </div>
              {/* <div className='col-12'></div> */}
            </div>
          </div>
        </Popup>

        <Popup
            open={showSeverity}
            modal
            closeOnDocumentClick={false}
            onClose={() => {
              setShowSeverity(false);
            }}
            className="symptom-popup"
          >
            <div
              style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                cursor: "pointer",
              }}
              onClick={() => {
                setShowSeverity(false);
              }}
            >
              <span className="material-symbols-outlined">close</span>
            </div>

            <div style={{ padding: "30px 50px" }}>
              <br />
              <div style={{ textAlign: "start", fontSize: "18px", marginTop: "10px" }}>
                <div style={{ textAlign: "center"}}>
                  <p>Diagnosis Name: <strong> { selectedOptionName } </strong></p>
                  <h2>Your Symptom Score</h2>
                  <br></br>
                  <div
                    style={{
                      width: "100px",
                      height: "100px",
                      borderRadius: "50%",
                      backgroundColor: getSeverityTier(symptomScore).bgColor,
                      border: `2px solid ${getSeverityTier(symptomScore).borderColor}`,
                      color: getScoreColor(symptomScore),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "24px",
                      fontWeight: "bold",
                      margin: "0 auto",
                      position: "relative",
                    }}
                  >
                    {symptomScore}
                  </div>
                  {severityDescription && (
                    <div style={{ marginTop: "10px" }}>
                      {severityDescription}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Popup>
      </div>
    // </div>
  );

}

export default PatientHomePage;

